/**
 * Synchronous pub/sub. Every cross-system message goes through here so systems
 * never import each other directly — that is what lets each subsystem be
 * developed and swapped independently.
 *
 * Handlers run in registration order. Throwing inside a handler is contained so
 * one broken listener cannot take down the frame.
 */
export class EventBus {
  constructor() { this.map = new Map(); this.anyHandlers = []; }

  on(type, fn) {
    let arr = this.map.get(type);
    if (!arr) { arr = []; this.map.set(type, arr); }
    arr.push(fn);
    return () => this.off(type, fn);
  }

  once(type, fn) {
    const off = this.on(type, (payload) => { off(); fn(payload); });
    return off;
  }

  off(type, fn) {
    const arr = this.map.get(type);
    if (!arr) return;
    const i = arr.indexOf(fn);
    if (i >= 0) arr.splice(i, 1);
  }

  /** Listen to everything — used by the debug console and the demo recorder. */
  onAny(fn) { this.anyHandlers.push(fn); return () => { const i = this.anyHandlers.indexOf(fn); if (i >= 0) this.anyHandlers.splice(i, 1); }; }

  emit(type, payload) {
    const arr = this.map.get(type);
    if (arr) {
      // iterate a copy so handlers may unsubscribe during dispatch
      for (const fn of arr.slice()) {
        try { fn(payload); } catch (e) { console.error(`[bus] handler for "${type}" threw`, e); }
      }
    }
    for (const fn of this.anyHandlers.slice()) {
      try { fn(type, payload); } catch (e) { console.error('[bus] wildcard handler threw', e); }
    }
  }

  clear() { this.map.clear(); this.anyHandlers.length = 0; }
}

/**
 * Canonical event names. Systems import these instead of typing strings so a
 * rename is a compile-time-ish failure rather than a silent no-op.
 */
export const EV = {
  // lifecycle
  READY: 'ready',
  PAUSE: 'pause',
  RESUME: 'resume',
  RESIZE: 'resize',
  QUALITY_CHANGED: 'quality:changed',

  // input / session
  POINTER_LOCK: 'input:pointerlock',
  ACTION: 'input:action',              // { action, pressed }

  // weapons
  WEAPON_FIRE: 'weapon:fire',          // { weapon, origin, dir, seedIndex }
  WEAPON_DRY: 'weapon:dry',
  WEAPON_RELOAD_START: 'weapon:reload:start',
  WEAPON_RELOAD_END: 'weapon:reload:end',
  WEAPON_SWITCH: 'weapon:switch',      // { from, to }
  WEAPON_ADS: 'weapon:ads',            // { active }
  SHELL_EJECT: 'weapon:shell',         // { position, velocity, caliber }

  // ballistics / combat
  IMPACT: 'combat:impact',             // { point, normal, surface, weapon, penetrated }
  TRACER: 'combat:tracer',             // { from, to, speed, caliber }
  DAMAGE: 'combat:damage',             // { target, amount, point, dir, hitbox, source }
  KILL: 'combat:kill',                 // { target, source, weapon, headshot, distance }
  HITMARKER: 'combat:hitmarker',       // { headshot, lethal }
  PLAYER_HURT: 'player:hurt',          // { amount, dir, source }
  PLAYER_DIED: 'player:died',

  // ai
  AI_ALERT: 'ai:alert',                // { agent, cause, position }
  AI_FIRE: 'ai:fire',
  AI_SPAWN: 'ai:spawn',
  AI_STATE: 'ai:state',                // { agent, from, to }

  // player movement
  FOOTSTEP: 'player:footstep',         // { position, surface, speed }
  LAND: 'player:land',                 // { position, impact }
  JUMP: 'player:jump',
  SLIDE_START: 'player:slide:start',
  MANTLE: 'player:mantle',

  // audio
  SOUND: 'audio:play',                 // { name, position?, volume?, pitch? }

  // ui
  NOTIFY: 'ui:notify',                 // { text, kind }
  OBJECTIVE: 'ui:objective',           // { text, done }
  SCORE: 'ui:score',                   // { delta, reason }
};
